import { useCallback } from 'react';
import type { MapMouseEvent, MapRef } from 'react-map-gl';
import type { Group } from '@/components/Dashboard/types';
import { handleClusterClick } from '@/components/Dashboard/utils';

interface UseMapEventsProps {
  mapRef: React.RefObject<MapRef | null>;
  selectedGroup: Group | null;
  setSelectedGroup: (group: Group | null) => void;
  type: string;
  setSelectedFarmer: (farmer: GeoJSON.GeoJsonProperties) => void;
}

export const useMapEvents = ({
  mapRef,
  selectedGroup,
  setSelectedGroup,
  type,
  setSelectedFarmer,
}: UseMapEventsProps) => {
  const handleMarkerClick = useCallback(
    (e: mapboxgl.MapMouseEvent, map: mapboxgl.Map, markerLayer: string) => {
      if (!map.getLayer(markerLayer)) {
        return;
      }

      const features = map.queryRenderedFeatures(e.point, {
        layers: [markerLayer],
      });

      if (!features || !features.length) return;

      const clickedFeature = features[0];
      if (markerLayer === 'forests-marker' || markerLayer === 'forests-dots') {
        return;
      }

      // Diagnostic (§8/§12): log only the KEY NAMES that survived boundary
      // validation, never the values - the values are farmer PII. This is the
      // empirical check for "which fields reach the popup": if a field the modal
      // reads (lirp_price, coffee_variety, plots_number…) is missing here, it was
      // stripped by safePinPropertiesSchema, not mis-keyed in the component.
      // eslint-disable-next-line no-console -- intentional keys-only boundary diagnostic (§8)
      console.debug(
        '[farmer marker] property keys received:',
        Object.keys(clickedFeature.properties ?? {}),
      );

      setSelectedFarmer(clickedFeature.properties);

      if (clickedFeature.geometry?.type === 'Point') {
        const coordinates = (clickedFeature.geometry as GeoJSON.Point).coordinates;
        map.flyTo({
          center: [coordinates[0], coordinates[1]],
          zoom: 14,
          speed: 1.5,
        });
      }
    },
    [setSelectedFarmer],
  );

  const handleGroupClick = useCallback(
    (e: MapMouseEvent, map: mapboxgl.Map, type: string) => {
      let groupLayers: string[] = [];

      if (type) {
        const layerName = `${type}-group`;
        if (map.getLayer(layerName)) {
          groupLayers = [layerName];
        }
      } else {
        ['farmers-group', 'factories-group', 'forests-group'].forEach((layer) => {
          if (map.getLayer(layer)) {
            groupLayers.push(layer);
          }
        });
      }

      if (groupLayers.length === 0) {
        return;
      }

      const groupFeatures = map.queryRenderedFeatures(e.point, {
        layers: groupLayers,
      });

      if (!groupFeatures || !groupFeatures.length) {
        return;
      }

      const clickedFeature = groupFeatures[0];
      const geometry = clickedFeature.geometry;

      if (geometry?.type === 'Point') {
        const coordinates = (geometry as GeoJSON.Point).coordinates;
        const layerId = clickedFeature.layer?.id || '';
        const groupType = layerId.replace('-group', '');

        const groupTitle = clickedFeature?.properties?.title;

        if (groupTitle) {
          setSelectedGroup({
            type: groupType,
            group: groupTitle,
          });

          if (groupType !== 'forests') {
            map.flyTo({
              center: [coordinates[0], coordinates[1]],
              zoom: 15,
              speed: 1.5,
              essential: true,
            });
          }
        }
      }
    },
    [setSelectedGroup, type],
  );

  const handleMapClick = useCallback(
    (e: mapboxgl.MapMouseEvent) => {
      const map = mapRef.current?.getMap();

      if (!map) {
        console.error('Map or styles are not loaded');
        return;
      }

      // 2026-09-17: was `&& type === ''`, which left farmer clusters and pins
      // dead whenever the FARMERS tab was active (clicks fell through to
      // handleGroupClick, whose layers are hidden once a group is selected).
      if (selectedGroup?.type === 'farmers') {
        handleClusterClick(e, map, 'farmers');
        handleMarkerClick(e, map, 'farmers-marker');
        return;
      } else if (selectedGroup?.type === 'forests') {
        // 2026-09-21: the yellow single-tree dots (below the pin zoom) open the
        // same photo as a pin.
        const markerFeatures = map.queryRenderedFeatures(e.point, {
          layers: ['forests-marker', 'forests-dots'].filter((l) => map.getLayer(l)),
        });
        if (markerFeatures?.length) {
          handleMarkerClick(e, map, 'forests-marker');
        } else {
          handleClusterClick(e, map, 'forests');
        }
        return;
      }

      handleGroupClick(e, map, type);
    },
    [mapRef, selectedGroup, type, handleMarkerClick, handleGroupClick],
  );

  const handleZoomOut = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (map) {
      map.flyTo({
        zoom: 4,
        speed: 2,
      });
    }
  }, [mapRef]);

  return {
    handleMapClick,
    handleZoomOut,
  };
};
